import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { useUser } from '~/modules/auth/hooks/useUser';

import { getItemData, getTrackVersions } from '../../api/dashboard';
import type { TrackIgnore } from '../../api/track-ignore';
import {
  getTrackIgnore,
  ignoreTrack,
  unignoreTrack,
} from '../../api/track-ignore';
import type { TrackLock } from '../../api/track-lock';
import { getTrackLock, lockTrack, unlockTrack } from '../../api/track-lock';
import type { DashboardItem, TrackVersion } from '../../types';
import { GeneralInfo } from './GeneralInfo';
import { VersionsInfo } from './Versions';

export interface DetailsProps {
  id: string;
}

type TabCode = 'general' | 'versions';

const Details = (props: DetailsProps): JSX.Element => {
  const { t } = useTranslation('label-perf');
  const { id } = props;
  const [data, setData] = useState<DashboardItem>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [activeTab, setActiveTab] = useState<TabCode>('general');
  const [versions, setVersions] = useState<TrackVersion[]>([]);
  const [trackLock, setTrackLock] = useState<TrackLock | null>(null);
  const [trackIgnore, setTrackIgnore] = useState<TrackIgnore | null>(null);
  const user = useUser();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);
        const results = await Promise.allSettled([
          getItemData(id),
          getTrackVersions(id),
          getTrackLock(id),
          getTrackIgnore(id),
        ]);

        const [
          itemDataResult,
          trackVersionsResult,
          trackLockResult,
          trackIgnoreResult,
        ] = results;

        // Check if getItemData was successful
        if (itemDataResult.status === 'fulfilled') {
          setData(itemDataResult.value);
        } else {
          setError('Failed to fetch item data');
        }

        // Check if getTrackVersions was successful
        if (trackVersionsResult.status === 'fulfilled') {
          setVersions(trackVersionsResult.value);
        } else {
          setError('Failed to fetch track versions');
        }

        // Handle getActiveTrackLock
        if (trackLockResult.status === 'fulfilled' && trackLockResult.value) {
          setTrackLock(trackLockResult.value);
        }

        // Handle getActiveTrackLock
        if (
          trackIgnoreResult.status === 'fulfilled' &&
          trackIgnoreResult.value
        ) {
          setTrackIgnore(trackIgnoreResult.value);
        }
      } catch (_error) {
        setError('An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleTrackLock = async (): Promise<void> => {
    try {
      const result = !trackLock ? await lockTrack(id) : await unlockTrack(id);
      setTrackLock(!trackLock ? result : null); // Update track lock state
    } catch (_error) {
      setError((_error as Error).message);
      // eslint-disable-next-line no-console
      console.error('Error creating track lock:', error);
    }
  };

  const handleTrackIgnore = async (): Promise<void> => {
    try {
      const result = !trackIgnore
        ? await ignoreTrack(id)
        : await unignoreTrack(id);
      setTrackIgnore(!trackIgnore ? result : null); // Update track lock state
    } catch (_error) {
      setError((_error as Error).message);
      // eslint-disable-next-line no-console
      console.error('Error creating track lock:', error);
    }
  };

  if (loading) {
    return (
      <Icon type="spinner" variant="solid" size="lg" className="fa-spin" />
    );
  }
  if (error) {
    return <p className="text-body-sm text-error-default">{error}</p>;
  }
  if (!data) {
    return (
      <p className="text-body-sm text-error-default">{t('trackNotFound')}</p>
    );
  }

  return (
    <div className="min-w-[20em]">
      {/* #ToDo: Refactor to use reusable tabs control */}
      {versions.length > 1 && (
        <div className="mb-md flex border-b border-emphasis-subtle">
          <button
            className={`p-sm ${activeTab === 'general' ? 'border-b border-tertiary-default' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('general')}
          </button>
          <button
            className={`p-sm ${activeTab === 'versions' ? 'border-b border-tertiary-default' : ''}`}
            onClick={() => setActiveTab('versions')}
          >
            {t('versions')}{' '}
            <span className="font-thin text-subtle">({versions.length})</span>
          </button>
        </div>
      )}

      {activeTab === 'general' && (
        <GeneralInfo
          currentUser={user}
          data={data}
          handleTrackIgnore={handleTrackIgnore}
          handleTrackLock={handleTrackLock}
          isrc={id}
          trackIgnore={trackIgnore}
          trackLock={trackLock}
        />
      )}
      {activeTab === 'versions' && <VersionsInfo versions={versions} />}
    </div>
  );
};

Details.displayName = 'Details';

export { Details };
